import _ from "lodash";
import {
  getMemoizedFunction,
  getMemoizedLangFunction,
  getYearDurationFormat,
} from "Utils";
import { CURRICULUM_TYPE_ORDER_MAPPING } from "Constants";
import { getUserInfo } from "Login/modules/LoginModule";
import { getIsStaticWidgetisedHomepageEnabled } from "Platform/modules/PlatformUtils";

export const COMBINED_PREFERENCE_KEY = "COMBINED";

export const FEED_TYPE = {
  TEACHER_COURSES: "TEACHER_COURSES",
  BLUEPRINT_COURSES: "BLUEPRINT_COURSES",
};

export const getTabsOptions = ({ t }) => {
  return [
    {
      value: FEED_TYPE.TEACHER_COURSES,
      label: t("common:teacher_course_plural"),
    },
    {
      value: FEED_TYPE.BLUEPRINT_COURSES,
      label: t("common:blueprint_course_plural"),
    },
  ];
};

export const getCourseFeedTableHeaders = ({ t, activeTab }) => {
  const isStaticWidgetisedHomepageEnabled = getIsStaticWidgetisedHomepageEnabled();

  if (activeTab === FEED_TYPE.BLUEPRINT_COURSES) {
    return [
      {
        key: "COURSE_NAME",
        value: t("common:blueprint_course_plural"),
        style: {
          padding: "12px",
          borderRadius: isStaticWidgetisedHomepageEnabled
            ? "8px 0px 0px"
            : "12px 0px 0px",
          width: "456px",
          borderRight: "0px",
        },
      },
      {
        key: "ASSOCIATED_TEACHER_COURSES",
        value: t("common:associated_label", {
          label: t("common:teacher_course_plural"),
        }),
        style: {
          padding: "12px",
          borderRadius: isStaticWidgetisedHomepageEnabled
            ? "0px 8px 0px 0px"
            : "0px 12px 0px 0px",
        },
      },
    ];
  }

  return [
    {
      key: "COURSE_NAME",
      value: t("common:teacher_course_plural"),
      style: {
        width: "348px",
        padding: "12px",
        borderRadius: isStaticWidgetisedHomepageEnabled
          ? "8px 0px 0px"
          : "12px 0px 0px",
      },
    },
    {
      key: "ASSOCIATED_CLASSES",
      value: t("common:associated_label", { label: t("common:class_plural") }),
      style: {
        padding: "12px",
        borderRadius: isStaticWidgetisedHomepageEnabled
          ? "0px 8px 0px 0px"
          : "0px 12px 0px 0px",
      },
    },
  ];
};

export const getConfigurableColumnsOptions = ({
  t,
  levelsCount,
  departmentsCount,
  hasMultipleCurriculumPrograms = false,
}) => {
  const options = [
    {
      key: "COURSE_CODE",
      value: t("course:course_code"),
      isDefaultVisible: true,
      isVisible: true,
    },
    {
      key: "GRADES",
      value: t("common:grade_level"),
      isDefaultVisible: true,
      isVisible: true,
    },
    {
      key: "SUBJECTS",
      value: t("common:subject"),
      isDefaultVisible: true,
      isVisible: true,
    },
    {
      key: "LEVELS",
      value: t("common:levels"),
      isVisible: levelsCount > 0,
    },
    {
      key: "DEPARTMENT",
      value: t("common:department"),
      isVisible: departmentsCount > 0,
    },
    {
      key: "GRADING_PERIODS",
      value: t("common:grading_period_plural"),
      isVisible: true,
    },
    {
      key: "SCHOOL",
      value: t("common:school"),
      isVisible: hasMultipleCurriculumPrograms,
    },
  ];

  return _.filter(options, option => option.isVisible);
};

export const getClassesFeedQueryVariables = ({
  organizationId,
  curriculumProgramIds,
  academicYearId,
  filters,
}) => {
  const searchText = _.get(filters, "searchText", "");

  return {
    organizationId,
    filters: {
      searchText,
      academicYears: [academicYearId],
      curriculumProgramIds,
      archivalState: "ALL",
      isLearningCourseClassFeed: true,
    },
  };
};

//This is used for jump to course feed queries
export const getCoursesFeedQueryVariables = getMemoizedFunction(
  ({ organizationId, curriculumProgramIds, academicYearId, filters }) => {
    const userId = getUserInfo()?.id;
    const searchText = _.get(filters, "searchText", "");

    const arrayFilters = {
      grades: _.get(filters, "grades", []),
      subjects: _.get(filters, "subjects", []),
      status: _.get(filters, "status", []),
      terms: _.get(filters, "terms", []),
      classes: _.get(filters, "classes", []),
      types: _.get(filters, "types", []),
    };

    const {
      grades,
      subjects,
      status,
      terms,
      classes,
      types,
    } = _.mapValues(arrayFilters, value => (value === "ALL" ? [] : value));

    return {
      organizationId,
      classesFilters: {
        status: "ACTIVE",
        userId,
        academicYears: [academicYearId],
      },
      gradeFilters: {
        academicYearIds: [academicYearId],
      },
      filters: {
        searchText,
        grades,
        subjects,
        states: status,
        academicTerms: terms,
        classIds: classes,
        academicYears: [academicYearId],
        curriculumProgramIds,
        types,
      },
    };
  }
);

export const getCoursesFeedQueryVariablesV2 = getMemoizedFunction(
  ({
    organizationId,
    curriculumProgramIds,
    academicYearId,
    filters,
    activeTab,
  }) => {
    const userId = getUserInfo()?.id;
    const searchText = _.get(filters, "searchText", "");

    const types = _.isEqual(activeTab, FEED_TYPE.BLUEPRINT_COURSES)
      ? ["BLUEPRINT_COURSE"]
      : ["TEACHER_COURSE"];

    const arrayFilters = {
      grades: _.get(filters, "grades", []),
      subjects: _.get(filters, "subjects", []),
      status: _.get(filters, "status", []),
      terms: _.get(filters, "terms", []),
      classes: _.get(filters, "classes", []),
      departments: _.get(filters, "departments", []),
      levels: _.get(filters, "levels", []),
      schools: _.get(filters, "schools", []),
    };

    const {
      grades,
      subjects,
      status,
      terms,
      classes,
      departments,
      levels,
      schools,
    } = _.mapValues(arrayFilters, value => (value === "ALL" ? [] : value));

    const selectedCurriculumProgramIds = _.isEmpty(schools)
      ? curriculumProgramIds
      : schools;

    return {
      organizationId,
      classesFilters: {
        status: "ACTIVE",
        userId,
        academicYears: [academicYearId],
      },
      filters: {
        searchText,
        grades,
        subjects,
        states: status,
        academicTerms: terms,
        classIds: classes,
        academicYears: [academicYearId],
        curriculumProgramIds: selectedCurriculumProgramIds,
        types,
        levelIds: levels,
        departmentIds: departments,
        fetchOnlyMappedLearningCourses: _.isEqual(
          filters.courseView,
          "MY_COURSES"
        ),
        isCustomOrderingEnabled: false,
      },
      termfilters: {
        academicYearIds: [academicYearId],
        curriculumProgramIds: selectedCurriculumProgramIds,
      },
      gradeFilters: {
        academicYearIds: [academicYearId],
      },
      orderBy: _.get(filters, "orderBy", "TITLE"),
      orderByDirection: _.get(filters, "orderByDirection", "ASC"),
    };
  }
);

export const getFilterOptions = ({
  t,
  filters,
  gradeOptions,
  subjectOptions,
  termOptions,
  statusOptions,
  departmentOptions,
  levelOptions,
  schoolOptions,
  showCourseViewFilter,
  hasMultipleCurriculumPrograms = false,
}) => {
  const options = {};
  const defaultSelectedOptions = {};

  const populateDefaultSelectedOptions = ({ filterName, filterOptions }) => {
    const filtersValue = _.get(filters, filterName, []);
    if (filtersValue == "ALL") {
      defaultSelectedOptions[filterName] = _.map(
        _.filter(filterOptions, option => !option.isSectionHeader),
        "value"
      );
    } else {
      defaultSelectedOptions[filterName] = filtersValue;
    }
  };

  if (hasMultipleCurriculumPrograms) {
    options["schools"] = {
      options: schoolOptions,
      title: t("common:school_plural"),
      allText: t("common:all_with_lowercase_label", {
        label: t("common:school_plural"),
      }),
    };
    populateDefaultSelectedOptions({
      filterName: "schools",
      filterOptions: schoolOptions,
    });
  }

  const courseViewOptions = [
    {
      label: t("common:all_courses"),
      value: "ALL_COURSES",
      key: "ALL_COURSES",
    },
    {
      label: t("common:my_courses_lower"),
      value: "MY_COURSES",
      key: "MY_COURSES",
    },
  ];

  if (showCourseViewFilter) {
    options["courseView"] = {
      options: courseViewOptions,
      title: t("common:courses"),
      uiType: "RADIO",
    };
    populateDefaultSelectedOptions({
      filterName: "courseView",
      filterOptions: courseViewOptions,
    });
  }

  // Grade
  if (!_.isEmpty(gradeOptions)) {
    options["grades"] = {
      options: gradeOptions,
      title: t("common:grade"),
      allText: t("common:all_with_lowercase_label", {
        label: t("common:grade_plural"),
      }),
    };
    populateDefaultSelectedOptions({
      filterName: "grades",
      filterOptions: gradeOptions,
    });
  }

  // Term
  if (!_.isEmpty(termOptions)) {
    options["terms"] = {
      options: termOptions,
      title: t("common:grading_period_plural"),
      allText: t("common:all_with_lowercase_label", {
        label: t("common:grading_period_plural"),
      }),
    };
    populateDefaultSelectedOptions({
      filterName: "terms",
      filterOptions: termOptions,
    });
  }

  // Subject
  if (!_.isEmpty(subjectOptions)) {
    options["subjects"] = {
      options: subjectOptions,
      title: t("common:subject"),
      allText: t("common:all_with_lowercase_label", {
        label: t("common:subject_plural"),
      }),
    };
    populateDefaultSelectedOptions({
      filterName: "subjects",
      filterOptions: subjectOptions,
    });
  }

  if (!_.isEmpty(departmentOptions)) {
    options["departments"] = {
      options: departmentOptions,
      title: t("common:department_plural"),
      allText: t("common:all_with_lowercase_label", {
        label: t("common:department_plural"),
      }),
    };
    populateDefaultSelectedOptions({
      filterName: "departments",
      filterOptions: departmentOptions,
    });
  }

  if (!_.isEmpty(levelOptions)) {
    options["levels"] = {
      options: levelOptions,
      title: t("common:levels"),
      allText: t("common:all_with_lowercase_label", {
        label: t("common:levels"),
      }),
    };
    populateDefaultSelectedOptions({
      filterName: "levels",
      filterOptions: levelOptions,
    });
  }

  // Status
  options["status"] = {
    options: statusOptions,
    title: t("common:status"),
    allText: t("common:all_with_lowercase_label", {
      label: t("course:course_lowercase_plural"),
    }),
  };
  populateDefaultSelectedOptions({
    filterName: "status",
    filterOptions: statusOptions,
  });

  return { options, defaultSelectedOptions };
};

export const getTermsOptionsMemoized = _.memoize(
  ({ termsData, alwaysGroup = false }) => {
    const formattedTermsData = _.map(termsData, term => ({
      ...term,
      formattedLabel: `${term.label} (${getYearDurationFormat({
        startDate: term.startDate,
        endDate: term.endDate,
      })})`,
    }));

    return getDataGroupedByCurriculum({
      data: formattedTermsData,
      valueField: "id",
      labelField: "formattedLabel",
      generateSortedData: true,
      alwaysGroup,
    });
  },
  ({ termsData, alwaysGroup = false }) =>
    `${_.map(
      termsData,
      ({ id, label, startDate, endDate }) =>
        `${id}-${label}-${startDate}-${endDate}`
    ).join(",")}-${alwaysGroup}`
);

export const getStatusOptionsMemoized = getMemoizedLangFunction(({ t }) => {
  return [
    {
      label: t("common:active_label", {
        label: t("course:course_lowercase_plural"),
      }),
      value: "ACTIVE",
    },
    {
      label: t("common:archive_with_label", {
        label: t("course:course_lowercase_plural"),
      }),
      value: "ARCHIVED",
    },
  ];
});

export const getFormattedFilterOptions = ({
  data,
  labelField = "title",
  valueField = "id",
  getLabel,
  alwaysGroup = false,
}) => {
  return getDataGroupedByCurriculum({
    data,
    valueField,
    labelField,
    generateSortedData: true,
    getLabel,
    alwaysGroup,
  });
};

export const getDataGroupedByCurriculum = ({
  data = [],
  valueField = "id",
  labelField = "name",
  generateSortedData = false,
  getLabel,
  alwaysGroup = false,
}) => {
  const resolveLabel = item => {
    if (_.isFunction(getLabel)) {
      return getLabel(item);
    }
    return _.get(item, labelField);
  };

  const groupedByCurriculum = _.groupBy(data, item =>
    _.get(item, "curriculumProgram.id")
  );

  if (_.size(groupedByCurriculum) <= 1 && !alwaysGroup) {
    return _.map(data, item => {
      return {
        label: resolveLabel(item),
        value: item[valueField],
      };
    });
  }

  const curriculumPrograms = [];
  _.forEach(groupedByCurriculum, (data, curriculumId) => {
    const firstItem = _.first(data);
    curriculumPrograms.push({
      id: curriculumId,
      label: _.get(firstItem, "curriculumProgram.label"),
      type: _.get(firstItem, "curriculumProgram.type"),
      options: data,
    });
  });

  let programsToProcess = curriculumPrograms;
  if (generateSortedData) {
    programsToProcess = _.orderBy(
      curriculumPrograms,
      [program => CURRICULUM_TYPE_ORDER_MAPPING[program.type]],
      ["asc"]
    );
  }

  const formattedData = [];
  _.forEach(programsToProcess, program => {
    formattedData.push({
      label: program.label,
      isSectionHeader: true,
      curriculumProgramId: program.id,
    });

    _.forEach(program.options, item => {
      formattedData.push({
        label: resolveLabel(item),
        value: item[valueField],
        curriculumProgramId: program.id,
      });
    });
  });

  return formattedData;
};

export const getSanitizedFiltersFromPreference = ({
  pref = {},
  grades,
  subjectsData,
  subjectGroupsData,
  genericTags,
  termsData,
  canViewAllCourses,
  curriculumPrograms,
  hasMultipleCurriculumPrograms = false,
  showCourseViewFilter,
}) => {
  const filterConfig = {
    grades,
    subjects: subjectsData,
    departments: subjectGroupsData,
    levels: genericTags,
    terms: termsData,
    ...(hasMultipleCurriculumPrograms
      ? {
          schools: curriculumPrograms,
        }
      : {}),
  };

  const sanitized = {};

  _.forEach(filterConfig, (validList, key) => {
    const value = _.get(pref, key);

    if (_.isEqual(value, "ALL")) {
      sanitized[key] = "ALL";
    } else if (_.isArray(value)) {
      const validIds = _.map(validList, "id");
      sanitized[key] = _.filter(value, id => _.includes(validIds, id));
    } else {
      // If no terms in pref, set default terms based on current date
      if (key === "terms") {
        let terms = [];
        if (!_.isEmpty(termsData)) {
          const currentDate = new Date();
          terms = _.map(
            _.filter(termsData, term => {
              const startDate = new Date(term.startDate);
              const endDate = new Date(term.endDate);
              return currentDate >= startDate && currentDate <= endDate;
            }),
            "id"
          );
        }
        sanitized.terms = terms;
      } else {
        sanitized[key] = [];
      }
    }
  });

  // Validate courseView preference
  const VALID_COURSE_VIEW_OPTIONS = ["MY_COURSES", "ALL_COURSES"];
  const prefCourseView = _.get(pref, "courseView");

  const defaultCourseView = canViewAllCourses ? "ALL_COURSES" : "MY_COURSES";

  const shouldApplyCoursePreference =
    showCourseViewFilter &&
    _.includes(VALID_COURSE_VIEW_OPTIONS, prefCourseView);

  sanitized.courseView = shouldApplyCoursePreference
    ? prefCourseView
    : defaultCourseView;

  const extraKeys = _.omit(pref, [..._.keys(filterConfig), "courseView"]);
  return _.assign({}, extraKeys, sanitized);
};

export const getSortByOptions = ({ t }) => {
  return [
    { label: t("common:a_to_z"), value: "ASC", key: "a_to_z" },
    { label: t("common:z_to_a"), value: "DESC", key: "z_to_a" },
  ];
};
